import axios from "axios";
import React, { useEffect, useState } from "react";
import { createBatch, uploadFile, getBatchFiles, downloadFile } from "../../../../services/FileUploadService";
import { useQuery } from "@tanstack/react-query";
import { Container, Button, ProgressBar, Form, Table, Spinner, Alert } from "react-bootstrap";

export const BulkFileUpload = () => {
    const [batchId, setBatchId] = useState(null);
    const [files, setFiles] = useState([]);
    const [progress, setProgress] = useState({});
    const [uploadStatus, setUploadStatus] = useState({});

    const { data: batchFiles, refetch, isFetching } = useQuery({
        queryKey: ["batchFiles", batchId],
        queryFn: () => getBatchFiles(batchId),
        enabled: !!batchId,
    });

    const handleCreateBatch = async () => {
        const batch = await createBatch();
        setBatchId(batch.batchId);
    };

    const handleFileUpload = async (file) => {
        try {
            setUploadStatus((prev) => ({ ...prev, [file.name]: "Uploading..." }));

            await uploadFile(batchId, file, (event) => {
                setProgress((prev) => ({
                    ...prev,
                    [file.name]: Math.round((event.loaded * 100) / event.total),
                }));
            });

            setUploadStatus((prev) => ({ ...prev, [file.name]: "Completed" }));
            refetch();
        } catch (error) {
            setUploadStatus((prev) => ({ ...prev, [file.name]: "Failed" }));
        }
    };

    const handleUploadAll = async () => {
        setProgress({});
        setUploadStatus({});
        for (const file of files) {
            await handleFileUpload(file);
        }
    };

    return (
        <Container className="mt-4">
            <h2 className="mb-3">Resumable File Upload</h2>

            <Button variant="primary" onClick={handleCreateBatch} disabled={!!batchId}>
                {batchId ? `Batch ID: ${batchId}` : "Create New Batch"}
            </Button>

            {batchId && (
                <div className="mt-3">
                    <Form.Group controlId="fileUpload">
                        <Form.Control type="file" multiple onChange={(e) => setFiles([...e.target.files])} />
                    </Form.Group>
                    <Button
                        variant="success"
                        className="mt-2"
                        onClick={handleUploadAll}
                        disabled={files.length === 0}
                    >
                        Upload Files
                    </Button>
                </div>
            )}

            {/* Progress Bars */}
            {Object.entries(progress).map(([fileName, percent]) => (
                <div key={fileName} className="mt-3">
                    <strong>{fileName} - {percent}%</strong>
                    <ProgressBar now={percent} striped variant={uploadStatus[fileName] === "Failed" ? "danger" : "info"} animated />
                    <Alert variant={uploadStatus[fileName] === "Failed" ? "danger" : "success"} className="mt-2 p-1 text-center">
                        {uploadStatus[fileName]}
                    </Alert>
                </div>
            ))}

            {/* File List Table */}
            {batchFiles && batchFiles.length > 0 && (
                <div className="mt-4">
                    <h4>Uploaded Files</h4>
                    {isFetching && <Spinner animation="border" size="sm" />}
                    <Table bordered hover className="mt-2">
                        <thead>
                            <tr>
                                <th>File Name</th>
                                <th>Status</th>
                                <th>Download</th>
                            </tr>
                        </thead>
                        <tbody>
                            {batchFiles.map((file) => (
                                <tr key={file.id}>
                                    <td>{file.originalFileName}</td>
                                    <td>
                                        <Alert variant={file.status === "COMPLETED" ? "success" : "warning"}>{file.status}</Alert>
                                    </td>
                                    <td>
                                        {file.status === "COMPLETED" && (
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => downloadFile(batchId, file.originalFileName)}
                                            >
                                                Download
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            )}
        </Container>
    );
};
